import type { Dispatch } from "react";
import type { Point, TableAction, TableState } from "./types";
import { TableResizer } from "./TableResizer";
import { Table } from "./Table";
import { useEffect, useRef, useState } from "react";
import { usePageScale } from "../editor/hooks";
import "./TableEditorOverlay.css";

type Props = {
  tableState: TableState;
  dispatch: Dispatch<TableAction>;
};

export function TableEditorOverlay({ tableState, dispatch }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const pageScale = usePageScale();
  const [selectionStart, setSelectionStart] = useState<Point | undefined>();

  const onSelectionStart = (event: React.MouseEvent) => {
    if (!overlayRef.current) return;

    const overlayRect = overlayRef.current.getBoundingClientRect();
    const x = (event.clientX - overlayRect.x) / pageScale;
    const y = (event.clientY - overlayRect.y) / pageScale;

    setSelectionStart({ x, y });
  };

  const onSelectionExtend = (event: MouseEvent) => {
    if (!selectionStart) return;
    if (!overlayRef.current) return;

    const overlayRect = overlayRef.current.getBoundingClientRect();
    const x = (event.clientX - overlayRect.x) / pageScale;
    const y = (event.clientY - overlayRect.y) / pageScale;

    dispatch({
      type: "selectTableCells",
      start: selectionStart,
      end: { x, y },
    });
  };

  const onSelectionEnd = (event: MouseEvent) => {
    onSelectionExtend(event);
    setSelectionStart(undefined);
  };

  useEffect(() => {
    document.addEventListener("mousemove", onSelectionExtend);
    return () => document.removeEventListener("mousemove", onSelectionExtend);
  });

  useEffect(() => {
    document.addEventListener("mouseup", onSelectionEnd);
    return () => document.removeEventListener("mouseup", onSelectionEnd);
  });

  return (
    <div
      ref={overlayRef}
      className="table-editor"
      style={{ left: tableState.position.x, top: tableState.position.y }}
    >
      <div className="table-editor-vertical-container">
        <div className="table-editor-column-resizers">
          <TableResizer
            size={0}
            dimension="x"
            onChange={(deltaX) =>
              dispatch({
                type: "moveTableX",
                x: deltaX,
                absolute: false,
              })
            }
          />
          {tableState.cols.map((col, colIndex) => (
            <TableResizer
              key={col.index}
              size={col.width}
              dimension="x"
              onChange={(newWidth) =>
                dispatch({
                  type: "resizeTableCol",
                  index: colIndex,
                  width: newWidth,
                })
              }
            />
          ))}
        </div>

        <div className="table-editor-horizontal-container">
          <div className="table-editor-row-resizers">
            <TableResizer
              size={0}
              dimension="y"
              onChange={(deltaY) =>
                dispatch({
                  type: "moveTableY",
                  y: deltaY,
                  absolute: false,
                })
              }
            />
            {tableState.rows.map((row, rowIndex) => (
              <TableResizer
                key={row.index}
                size={row.height}
                dimension="y"
                onChange={(height) =>
                  dispatch({
                    type: "resizeTableRow",
                    index: rowIndex,
                    height,
                  })
                }
              />
            ))}
          </div>

          <div
            className="table-editor-table-container"
            onMouseDown={onSelectionStart}
          >
            <Table tableState={tableState} dispatch={dispatch} />
          </div>
        </div>
      </div>
    </div>
  );
}
